/** Account-scoped Home preferences. The backend owns persistence and identity. */
import { request } from '@/lib/api';
import { parseHomeNetworkPreference, type HomeNetwork } from '@/lib/homePreferences';
import { getSlice, setHomeNetworkPreference } from '@/store/store';

const PATH = '/v1/me/preferences';
const MAX_RECONCILIATIONS = 2;

interface PreferenceController {
  choose: (network: HomeNetwork | null) => void;
  retry: () => void;
  stop: () => void;
}

let active: PreferenceController | null = null;

/**
 * One controller per authenticated session, including reload resumption. Account
 * checks fence the gap before React cleanup. Only explicit choices become writes.
 * Each assignment carries the last server revision, so a timed-out old request
 * cannot overwrite a newer committed preference on another connection/device.
 */
export function startHomePreferences(): () => void {
  active?.stop();
  const owner = getSlice('accounts').current;
  if (!owner.authenticated) return () => {};

  const abort = new AbortController();
  let stopped = false;
  let choiceRevision = 0;
  let confirmedRevision: number | null = null;
  let dirty = false;
  let working = false;
  let desired: HomeNetwork | null = getSlice('ui').homeNetwork;

  function current(): boolean {
    const account = getSlice('accounts').current;
    return !stopped && !abort.signal.aborted && account.authenticated &&
      account.sessionEpoch === owner.sessionEpoch &&
      account.orgId === owner.orgId && account.userId === owner.userId;
  }

  async function synchronize(): Promise<void> {
    if (!current() || working) return;
    working = true;
    let reconciliations = 0;
    setHomeNetworkPreference({
      homeNetworkStatus: dirty ? 'saving' : 'loading',
      homeNetworkError: null,
    });
    try {
      while (current()) {
        if (confirmedRevision === null) {
          let loaded;
          try {
            loaded = parseHomeNetworkPreference(await request<unknown>(PATH, {
              signal: abort.signal,
            }));
          } catch {
            if (current()) setHomeNetworkPreference({
              homeNetworkStatus: 'error',
              homeNetworkError: dirty ? 'save' : 'load',
            });
            return;
          }
          if (!current()) return;
          confirmedRevision = loaded.revision;
          if (!dirty) {
            desired = loaded.homeNetwork;
            setHomeNetworkPreference({
              homeNetwork: desired,
              homeNetworkStatus: 'saved',
              homeNetworkError: null,
            });
            return;
          }
          // Even an equal read needs the explicit CAS assignment below: its
          // revision advance fences an older timed-out request still in flight.
        }

        const startedChoice = choiceRevision;
        const value = desired;
        const expectedRevision = confirmedRevision;
        try {
          const saved = parseHomeNetworkPreference(await request<unknown>(PATH, {
            method: 'PATCH',
            body: { home_network: value, expected_revision: expectedRevision },
            signal: abort.signal,
            retry: false,
          }));
          if (!current()) return;
          if (saved.homeNetwork !== value || saved.revision !== expectedRevision + 1) {
            throw new Error('Preference acknowledgement did not match the assignment.');
          }
          confirmedRevision = saved.revision;
          if (choiceRevision !== startedChoice) continue;
          dirty = false;
          setHomeNetworkPreference({ homeNetworkStatus: 'saved', homeNetworkError: null });
          return;
        } catch {
          if (!current()) return;
          confirmedRevision = null;
          // A conflict or transport failure may follow a committed assignment.
          // Re-read the revision and assign only the latest explicit choice.
          // Bounded reconciliation prevents an unavailable service from looping.
          if (reconciliations++ < MAX_RECONCILIATIONS) continue;
          setHomeNetworkPreference({ homeNetworkStatus: 'error', homeNetworkError: 'save' });
          return;
        }
      }
    } finally {
      working = false;
    }
  }

  const controller: PreferenceController = {
    choose(network) {
      if (!current()) return;
      choiceRevision += 1;
      desired = network;
      dirty = true;
      setHomeNetworkPreference({
        homeNetwork: network,
        homeNetworkStatus: 'saving',
        homeNetworkError: null,
      });
      void synchronize();
    },
    retry() {
      confirmedRevision = null;
      void synchronize();
    },
    stop() {
      stopped = true;
      abort.abort();
      if (active === controller) active = null;
    },
  };
  active = controller;
  void synchronize();
  return controller.stop;
}

/** Only explicit Home picker actions call this; hydration never invokes PATCH. */
export function chooseHomeNetwork(network: HomeNetwork | null): void {
  active?.choose(network);
}

/** Reconcile a failed load or save against the current server revision. */
export function retryHomePreferences(): void {
  active?.retry();
}
